<template>
  <div class="w-full">
    <!-- Toolbar -->
    <div class="flex w-full gap-2 p-3 border-b border-border-normal">
      <div v-if="loading.target" class="w-24 h-8 skeleton rounded-md" />
      <div v-else class="flex items-start flex-shrink-0 fade-in">
        <button class="base-btn flex items-center gap-0.5 p-1.5"
        @click="show.targetModal = true">
          <TargetGoalIcon class="text-icon-normal" />
          <BaseText type="label" size="sm" class="text-text-muted ml-1">
            Targets
          </BaseText>
          <MaximizeIcon class="text-icon-normal" />
        </button>
      </div>
      <!-- Launch date selector -->
      <div v-if="loading.creativeReport" class="w-44 h-8 skeleton rounded-md" />
      <div v-else class="flex items-start flex-shrink-0 fade-in">
        <div v-on-clickaway="() => { show.dateRangeSelector = false }" class="relative">
          <button class="base-btn flex items-center gap-1.5 pl-2 py-1.5 pr-2.5 rounded-md flex-shrink-0"
          :class="show.dateRangeSelector ? 'bg-neutral-25' : 'bg-transparent hover:bg-neutral-25'"
          @click="show.dateRangeSelector = !show.dateRangeSelector">
            <CalendarScheduleIcon class="text-icon-normal" />
            <BaseText size="sm" class="text-text-normal">
              Launched
            </BaseText>
            <BaseText type="label" size="sm" class="text-text-muted">
              {{ dateFilterLabel }}
            </BaseText>
          </button>
          <transition>
            <div v-if="show.dateRangeSelector" class="date-selector-dropdown">
              <DateRangeSelectorDropdown
                :selected-date-range.sync="selectedDateRange"
                :is-lens-report="true"
                :excluded-presets="['allTime', 'last365Days']"
                :min-date="minSelectorDate"
                info-label="Only show ads launched between these dates"
                close-on-apply
                @close="show.dateRangeSelector = false"
              />
            </div>
          </transition>
        </div>
      </div>
      <!-- Add Filter Button -->
      <div v-if="!loading.creativeReport" v-on-clickaway="() => { closeFilterCreation() }" class="relative fade-in">
        <button class="flex items-center p-1 rounded-md transition-colors duration-100 whitespace-nowrap"
        :class="isBuildingFilter ? 'bg-neutral-25' : 'bg-transparent hover:bg-neutral-25'"
        @click="toggleFilterCreation">
          <FilterIcon class="h-4" />
          <BaseText type="label" size="sm" class="text-text-muted pl-1 py-0.5 pr-2">
            Add Filter
          </BaseText>
        </button>
        <!-- Filter Type Selector Dropdown -->
        <transition>
          <div v-if="showFilterSelector" class="filter-selector-dropdown">
            <PropertySelectDropdown
              search-placeholder="Search Filters"
              @optionSelected="handleFilterOptionSelected"
              @close="closeFilterCreation"
            />
          </div>
        </transition>
        <!-- Filter Query Builder -->
        <transition>
          <div v-if="builderInitFilterOption" class="filter-query-builder">
            <LensFilterQueryBuilder
              :init-filter-option="builderInitFilterOption"
              :currency-region="lens?.currency"
              @createFilter="handleFilterCreated"
              @close="closeFilterCreation"
            />
          </div>
        </transition>
      </div>
      <!-- Applied Filters List -->
      <div class="flex flex-wrap gap-2">
        <div v-for="(filter, index) in appliedFilters" :key="`applied-filter-${index}`" 
        class="relative fade-in" :ref="`applied-filter-${index}`"
        v-on-clickaway="() => { if (editingFilterIndex === index) editingFilterIndex = null }">
          <AppliedFilter 
            :filterConditions="filter"
            :isEditing="editingFilterIndex === index"
            :currency-region="lens?.currency"
            @selected="toggleEditAppliedFilter(index)"
            @remove="handleFilterRemoved(index)"
          />
          <!-- Edit Filter Query Builder -->
          <transition>
            <div v-if="editingFilterIndex === index" class="edit-filter-query-builder"
            :class="getEditFilterDropdownPositionClasses(index)">
              <LensFilterQueryBuilder 
                :initFilterConditions="filter"
                :currency-region="lens?.currency"
                @createFilter="handleEditAppliedFilter($event, index)"
                @close="editingFilterIndex = null"
              />
            </div>
          </transition>
        </div>
      </div>
      <!-- Ad Account Selector -->
      <div class="ml-auto">
        <div v-if="!adAccountOptions" class="skeleton w-64 h-9 rounded-md" />
        <AdAccountFilterSelector 
          v-else
          class="fade-in"
          :ad-account-options="adAccountOptions"
          :selected-account-ids="selectedAccountIds"
          dropdown-align-override="right"
          @selection-change="$emit('ad-account-selection-change', $event)"
        />
      </div>
    </div>
    <!-- Creative Test Overview -->
    <div class="p-3">
      <LensCreativeTestOverview
        :lens="lens"
        :selected-category-group="selectedCategoryGroup"
        :chart-data="chartData"
        :max-chart-values="maxChartValues"
        :category-insight-counts="categoryInsightCounts"
        :chart-thresholds="chartThresholds"
        :fetching-data="loading.chartData"
        :date-filter-label="dateFilterLabel"
        :is-active-tab="isActiveTab"
        :lighthouse-config="lighthouseConfig"
        @show-insights-drawer="handleShowDetailsDrawer($event)"
        @filter-by-category="handleFilterByCategory"
      />
    </div>
    <!-- Creative Test Table -->
    <div class="p-3">
      <div class="flex items-center gap-3">
        <div class="flex items-center gap-0.5 p-0.5 rounded-md bg-neutral-50">
          <button class="graph-selector-atom" :class="{ 'active': graphType === GRAPH_TYPES.TABLE }"
          @click="graphType = GRAPH_TYPES.TABLE">
            <PresetIcon class="text-icon-normal" />
          </button>
          <button class="graph-selector-atom" :class="{ 'active': graphType === GRAPH_TYPES.GRID }"
          @click="graphType = GRAPH_TYPES.GRID">
            <GridGraphIcon class="text-icon-normal" />
          </button>
        </div>
        <LensTableOptions
          v-if="graphType === GRAPH_TYPES.TABLE"
          class="flex-1"
          hide-results-per
          hide-add-metric
          is-creative-test
          :table-pagination="pagination"
          :table-columns="creativeReport.table_columns"
          :table-config="creativeReport.table_config"
          :selected-columns="selectedColumns[graphType]"
          :search-query="searchQuery"
          :can-scroll-left="tableScrollState.canScrollLeft"
          :can-scroll-right="tableScrollState.canScrollRight"
          :handle-scroll="handleTableScroll"
          @searchChanged="searchQuery = $event"
          @reportChanged="handleCreativeReportChange"
        />
        <div v-else class="flex flex-wrap gap-2 items-start">
          <div v-for="(kpi, index) in selectedColumns[graphType]" :key="`selected-kpi-${index}`" class="selected-kpi">
            <!-- KPI name -->
            <BaseText type="label" size="sm" class="text-text-muted capitalize pl-1.5 whitespace-nowrap">
              {{ getMetricLookup[kpi]?.name || `Unknown Metric (${kpi?.key ?? kpi})` }}
            </BaseText>
            <!-- Remove button -->
            <button class="group w-6 h-6 flex items-center justify-center"
            @click="handleRemoveMetric(kpi)">
              <TagRemoveIcon :width="16" class="text-text-subdued transition-colors duration-100 group-hover:text-icon-muted" />
            </button>
          </div>
          <div class="relative" v-on-clickaway="() => { show.addMetricDropdown = false }">
            <button ref="addMetricBtn" class="group flex items-center p-0.5 hover:bg-background-normal rounded-md transition-colors text-text-muted"
            @click="show.addMetricDropdown = !show.addMetricDropdown">
              <div class="w-6 h-6 flex items-center justify-center">
                <PlusIcon :width="16" :height="16" class="transition-colors text-icon-normal" />
              </div>
              <BaseText type="label" size="sm" class="pl-0.5 py-0.5 pr-2 text-text-muted">
                Add Metric
              </BaseText>
            </button>
            <!-- Add Metric Dropdown -->
            <transition name="add-metric"
            :leave-active-class="disableAddMetricFadeOut ? '' : 'add-metric-leave-active'"
            :leave-to-class="disableAddMetricFadeOut ? '' : 'add-metric-leave-to'">
              <div v-if="show.addMetricDropdown" class="add-metric-dropdown">
                <PropertySelectDropdown
                  search-placeholder="Search Metrics"
                  metrics-only
                  emit-keys-only
                  :selections="selectedMetrics"
                  :excluded-categories="['tags']"
                  @optionSelected="handleAddMetric"
                  @close="show.addMetricDropdown = false"
                />
              </div>
            </transition>
          </div>
        </div>
      </div>
      <div v-if="!pagination.totalItems" class="grid grid-cols-12 grid-rows-6 gap-0 border relative border-neutral-50 mr-3 mt-3 rounded-lg">
        <!-- Loading text-->
        <div v-if="isLoading || (pagination.totalItems === 0 && loading.launchGroups)"
        class="absolute w-full h-full flex flex-col items-center justify-center gap-1 text-center">
          <div>
            <BaseLoadingSpinnerCircle :width="24" :height="24" class="text-text-muted" />
          </div>
          <BaseText class="text-text-muted" type="label" size="sm">
            Loading Data...
          </BaseText>
          <BaseText class="text-text-normal w-64" size="sm">
            One moment while we load the data. This might take up to a minute.
          </BaseText>
        </div>
        <div v-else-if="!loading.launchGroups && !pagination.totalItems" 
        class="absolute w-full h-full flex flex-col items-center justify-center gap-1 text-center">
          <div>
            <WarningIcon class="text-icon-normal" />
          </div>
          <BaseText class="text-text-muted" type="label" size="sm">
            No data found
          </BaseText>
        </div>
        <!-- Grid background -->
        <div v-for="index in 36" :key="index" :class="{ 'border-b': index <= 30, 'border-r': index % 30 !== 0 }"
        class="border-neutral-50 h-20 col-span-2"/>
      </div>
      <!-- Table -->
      <div v-else-if="pagination.totalItems > 0" class="fade-in">
        <LensMetricTable
          v-if="graphType === GRAPH_TYPES.TABLE"
          ref="metricTable"
          class="mt-4"
          :grouped-by="'ad_name'"
          :loading="loading.launchGroups"
          :lens-currency="lens.currency"
          :selected-category-group="selectedCategoryGroup"
          :categorized-rows="categorizedData"
          :table-columns="creativeReport.table_columns"
          :current-sort="creativeReport.sorted_column"
          :search-query="searchQuery"
          :graph-info="graphInfo"
          :table-config="creativeReport.table_config"
          :table-pagination="pagination"
          hide-pagination
          enable-float-header-toggle
          :float-header-enabled.sync="floatColumnHeader"
          @scroll-state-change="handleScrollStateChange"
          @showGroupBreakdown="handleShowBreakdown"
          @showDetailsDrawer="handleShowDetailsDrawer"
          @reportChanged="handleCreativeReportChange"
        />
        <div v-else class="mt-4">
          <LensCreativeGrid
            :data="categorizedData"
            :selected-columns="selectedColumns[GRAPH_TYPES.GRID]"
            @showDetailsDrawer="handleShowDetailsDrawer"
          />
        </div>
      </div>
    </div>
    <!-- Modals & Drawers -->
    <GroupBreakdownDrawer
      v-if="showGroupBreakdown"
      :loading="loading.groupBreakdown"
      :group-data="groupBreakdownData"
      :current-lens="lens"
      :current-report="creativeReport"
      :selected-date-range="selectedDateRange"
      :report-summary-stats="categorizedData.summary_stats"
      :graph-consts="{ maxRows: MAX_GRAPH_ROWS, maxCols: MAX_GRAPH_COLS }"
      :init-selected-columns="breakdownSelectedColumns"
      :init-graph-type="GRAPH_TYPES.GRID"
      :is-parent-drawer="showCreativeDetails"
      header-title="Creative Asset"
      @showDetailsDrawer="handleShowDetailsDrawer($event)"
      @close="handleCloseBreakdown"
    />
    <AdInsightsDrawer
      v-if="showCreativeDetails"
      :brand="{ ...lens, name: lens?.company_name ?? lens?.name ?? ''}"
      is-ad-level
      :loading="loading.adDetails"
      :loading-metrics="loading.adDetailsMetrics"
      :date-range="selectedDateRange"
      :ad-group-data="adDetailsData"
      :summary-stats="categorizedData.summary_stats"
      :all-ads="adDetailsData?.ads"
      :selected-columns="creativeReport?.table_columns || []"
      :is-child-drawer="showGroupBreakdown"
      @refetch-insights="handleShowDetailsDrawer(adDetailsRow, $event)"
      @close="adDetailsData = null"
    />
    <CreativeTargetModal
      v-if="show.targetModal"
      :lens-id="lens?.id || $route.params.lensId"
      :is-new-target="isNewTarget"
      :currency-region="lens.currency"
      :launch-date-range="selectedDateRange"
      :raw-lighthouse-config="rawLighthouseConfig"
      @save="handleConfigSave"
      @close="handleTargetClose"
    />
    <ReportEditSavePrompt
      ref="reportEditSavePrompt"
      :loading-info="editLoading"
      :original-report-config="originalReportConfig"
      :changed-report-config="reportConfig"
      @save="handleSaveReport"
      @cancel="handleReportCancel"
    />
  </div>
</template>

<script>
import LensAPI from '@/api/lens'
import { mapGetters, mapActions } from 'vuex'
import { formatCustomDateRangeLabel, getMatchingPresetName, floorDate } from '@/utils/dateUtils'
import { getReportStateFromConfig, applyChangeToReportConfig, transformFieldValueAllPlatforms } from '@/utils/lens/reportDataUtils'
import { parseFiltersFromQuery, createFilterQuery, parseDateRangeFromQuery, createDateRangeQuery } from '@/utils/lens/filters/filterQueryUtils'
import dynamicFilterOptions from '@/utils/lens/filters/dynamicFilterOptions'
import getFilterTypeOperations from '@/utils/lens/filters/filterTypeOperations'

import { getGoalMetricKey } from '@/utils/lens/dataSourceUtils'
import { mixin as clickaway } from 'vue-clickaway2'
import axios from 'axios'
import _ from 'lodash'

// Components
import LensMetricTable from '../../components/lens/table/LensMetricTable.vue'
import LensTableOptions from '../../components/lens/table/LensTableOptions.vue'
import CreativeTargetModal from '../../components/lens/CreativeTargetModal.vue'
import DateRangeSelectorDropdown from '../../components/search/TopBarComponents/DateRangeSelectorDropdown.vue'
import LensCreativeTestOverview from '../../components/lens/graphs/LensCreativeTestOverview.vue'
import LensCreativeGrid from '../../components/lens/table/LensCreativeGrid.vue'
import PropertySelectDropdown from '../../components/lens/filters/PropertySelectDropdown.vue'
import LensFilterQueryBuilder from '../../components/lens/filters/LensFilterQueryBuilder.vue'
import AppliedFilter from '../../components/lens/filters/AppliedFilter.vue'
import AdInsightsDrawer from '../../components/lens/report/AdInsightsDrawer.vue'
import ReportEditSavePrompt from '../../components/lens/report/ReportEditSavePrompt.vue'
import GroupBreakdownDrawer from '../../components/lens/report/GroupBreakdownDrawer.vue'
import AdAccountFilterSelector from '../../components/lens/AdAccountFilterSelector.vue'

// Icons
import TargetGoalIcon from '../../components/globals/Icons/TargetGoalIcon.vue'
import MaximizeIcon from '../../components/globals/Icons/MaximizeIcon.vue'
import PresetIcon from '../../components/globals/Icons/LensIcons/PresetIcon.vue'
import GridGraphIcon from '../../components/globals/Icons/LensIcons/graphs/GridGraphIcon.vue'
import CalendarScheduleIcon from '../../components/globals/Icons/CalendarScheduleIcon.vue'
import WarningIcon from '../../components/globals/Icons/WarningIcon.vue'
import TagRemoveIcon from '../../components/globals/Icons/TagRemoveIcon.vue'
import PlusIcon from '../../components/globals/Icons/PlusIcon.vue'
import FilterIcon from '../../components/globals/Icons/FilterIcons/FilterIcon.vue'

export default {
  name: 'LensCreativeTestView',
  components: {
    LensMetricTable,
    LensTableOptions,
    LensCreativeTestOverview,
    CreativeTargetModal,
    DateRangeSelectorDropdown,
    PropertySelectDropdown,
    LensFilterQueryBuilder,
    AppliedFilter,
    AdInsightsDrawer,
    LensCreativeGrid,
    ReportEditSavePrompt,
    GroupBreakdownDrawer,
    AdAccountFilterSelector,
    
    // Icons
    TargetGoalIcon,
    WarningIcon,
    GridGraphIcon,
    PlusIcon,
    MaximizeIcon,
    PresetIcon,
    TagRemoveIcon,
    CalendarScheduleIcon,
    FilterIcon
  },
  mixins: [clickaway],
  props: {
    lens: {
      type: Object,
      default: () => { }
    },
    adAccountOptions: {
      required: true
    },
    selectedAccountIds: {
      type: Array,
      required: true
    }
  },
  data () {
    const GRAPH_TYPES = {
      GRID: 'grid',
      TABLE: 'table'
    }
    const MAX_GRAPH_ROWS = {
      bar: 8,
      line: 8,
      grid: 12
    }
    const MAX_GRAPH_COLS = {
      bar: 4,
      line: 2,
      grid: 12
    }

    return {
      // Constants
      MAX_GRAPH_ROWS,
      MAX_GRAPH_COLS,
      GRAPH_TYPES,

      // Selected Insight Category Group
      selectedCategoryGroup: 'all',

      // ===== Lighthouse Config State ====== \\
      isNewTarget: false,
      rawLighthouseConfig: {},

      // ==== Creative Tests Chart State ==== \\
      chartData: [],
      maxChartValues: null,
      categoryInsightCounts: null,
      isActiveTab: true,

      // =========== Report State =========== \\
      reportId: null,
      reportConfig: {},
      originalReportConfig: {},
      creativeReport: {},
    
      // ========= Table/Grid State ========= \\
      graphType: GRAPH_TYPES.TABLE,
      categorizedData: {},
      selectedColumns: {
        [GRAPH_TYPES.GRID]: ['spend', 'roas'],
        [GRAPH_TYPES.TABLE]: ['spend', 'roas']
      },
      searchQuery: '',
      pagination: {
        currentPage: 1,
        limit: 10,
        totalItems: 0,
        totalPages: 0
      },

      // =========== Filters State ========== \\
      appliedFilters: [],
      builderInitFilterOption: null,

      // Date range
      selectedDateRange: {
        start: null,
        end: null,
        isRelativeToToday: true
      },
      dateFilterLabel: 'Last 14 days',

      // =========== Drawer State =========== \\
      adDetailsData: null,
      adDetailsRow: null,
      groupBreakdownData: null,

      // ============= UI State ============= \\
      firstInitComplete: false,
      isLoading: false,
      loading: {
        target: true,
        creativeReport: true,
        launchGroups: false,
        chartData: true,
        adDetails: false,
        adDetailsMetrics: false,
        groupBreakdown: false
      },
      editLoading: {
        type: null,
        value: false
      },
      show: {
        targetModal: false,
        dateRangeSelector: false,
        addMetricDropdown: false
      },
      disableAddMetricFadeOut: false,
      // Filters
      editingFilterIndex: null,
      showFilterSelector: false,
      // Table
      tableScrollState: {
        canScrollLeft: false,
        canScrollRight: false
      },
      floatColumnHeader: localStorage.getItem('lens_table_float_column_header') === 'true'
    }
  },
  async mounted () {
    this.isLoading = true

    await this.fetchCreativeTargets()

    if (!this.isNewTarget) {
      await this.fetchCreativeReport()
    }
    this.isLoading = false
  },
  activated () {
    if (this.isNewTarget) {
      this.show.targetModal = true
    }
    this.isActiveTab = true
  },
  deactivated () {
    this.isActiveTab = false
  },
  watch: {
    selectedAccountIds () {
      this.initCreativeReportData()
    },
    appliedFilters (filters) {
      this.handleFiltersChange(filters, this.selectedDateRange)
    },
    selectedDateRange (dateRange) {
      this.handleFiltersChange(this.appliedFilters, dateRange)
      if (!dateRange?.start || !dateRange?.end) {
        this.dateFilterLabel = 'All Time'
      } else if (dateRange.isRelativeToToday) {
        this.dateFilterLabel = getMatchingPresetName(dateRange) || formatCustomDateRangeLabel(dateRange.start, dateRange.end)
      } else {
        this.dateFilterLabel = formatCustomDateRangeLabel(dateRange.start, dateRange.end)
      }
    },
    floatColumnHeader (value) {
      localStorage.setItem('lens_table_float_column_header', value ? 'true' : 'false')
    }
  },
  computed: {
    ...mapGetters('MetricsModule', ['getMetricLookup', 'getPresets', 'getAppliedPreset', 'getIsCustomPreset', 'getMetricsPlatform']),
    lighthouseConfig () {
      if (_.isEmpty(this.rawLighthouseConfig)) return {}
      
      const { tiktok_config, ...metaConfig } = this.rawLighthouseConfig
      if (this.getMetricsPlatform === 'meta') {
        return metaConfig
      } else {
        return {
          ...metaConfig,
          scaling: tiktok_config?.scaling || [[{ key: 'spend', operation: 'greater_than', value: 100 }]],
          winners: tiktok_config?.winners || [[{ key: 'roas', operation: 'greater_than', value: 1 }]],
          losers: tiktok_config?.losers || [[{ key: 'spend', operation: 'less_than', value: 10 }]]
        }
      }
    },
    breakdownSelectedColumns () {
      return {
        bar: this.creativeReport.table_columns.map((kpi) => kpi.key).slice(0, 2),
        grid: this.selectedColumns[this.GRAPH_TYPES.GRID].slice(0, 2)
      }
    },
    showGroupBreakdown () {
      return this.loading.groupBreakdown || this.groupBreakdownData !== null
    },
    showCreativeDetails () {
      return this.loading.adDetails || this.adDetailsData !== null
    },
    selectedMetrics () {
      return this.graphType === this.GRAPH_TYPES.TABLE ? this.creativeReport.table_columns : this.selectedColumns[this.graphType]
    },
    // TOOD @Sam: correct data
    graphInfo () {
      return {
        selectedGraph: this.graphType

      }
    },
    kpiColumns () {
      const graphType = this.graphType
      if (!this.selectedColumns[graphType] || this.selectedColumns[graphType].length === 0) return []
      return this.selectedColumns[graphType].map((col) => col.key)
    },
    chartThresholds () {
      if (!this.lighthouseConfig) return null
      const { scaling, winners, losers } = this.lighthouseConfig
      if (!scaling?.length || !winners?.length || !losers?.length) return null
      return {
        scaling: scaling[0][0],
        winners: winners[0][0],
        losers: losers[0][0]
      }
    },
    isBuildingFilter () {
      return this.showFilterSelector || this.builderInitFilterOption
    },
    minSelectorDate () {
      const minDateStr = this.lens?.metadata?.min_event_date // YYYY-MM-DD
      return minDateStr
        ? floorDate(new Date(minDateStr))
        : floorDate(new Date(new Date().setDate(new Date().getDate() - 365))) // Default to 365 days ago
    },
    targetCategoryFilterOptions () {
      // The cursed method we have to recreate the applied filters that match the insight category conditions
      // Value should be a one-and-done computation when config is loaded/changed
      if (!this.lighthouseConfig) return {}
      const { scaling, winners, losers } = this.lighthouseConfig
      const scalingCondition = scaling?.[0]?.[0]
      const winnersCondition = winners?.[0]?.[0]
      const losersCondition = losers?.[0]?.[0]

      const uncategorizedOptions = dynamicFilterOptions().flatMap(category => category.options)
      const relevantOptions = uncategorizedOptions.filter(option => 
        [scalingCondition.key, winnersCondition.key, losersCondition.key].includes(option.key)
      )
      const scalingOption = relevantOptions.find(option => option.key === scalingCondition.key)
      const winnersOption = relevantOptions.find(option => option.key === winnersCondition.key)
      const losersOption = relevantOptions.find(option => option.key === losersCondition.key)

      const typeOperations = getFilterTypeOperations(scalingOption.input.type)
      const inverseOperationKey = (operationKey) => (operationKey === 'greater_than' ? 'less_than' : 'greater_than')
      const getOperation = (operationKey) => typeOperations.find(operation => operation.key === operationKey)

      return {
        scaling: [
          [{ property: scalingOption, operation: getOperation(scalingCondition.operation), value: scalingCondition.value }],
          [{ property: winnersOption, operation: getOperation(inverseOperationKey(winnersCondition.operation)), value: winnersCondition.value }]
        ],
        winners: [
          [{ property: scalingOption, operation: getOperation(scalingCondition.operation), value: scalingCondition.value }],
          [{ property: winnersOption, operation: getOperation(winnersCondition.operation), value: winnersCondition.value }]
        ],
        losers: [
          [{ property: losersOption, operation: getOperation(losersCondition.operation), value: losersCondition.value }]
        ]
      }
    }
  },
  methods: {
    ...mapActions('MetricsModule', ['setPresetById', 'updateIsCustomPreset']),

    // ============================================================================ \\
    // ======================= LIGHTHOUSE DATA METHODS ============================ \\
    // ============================================================================ \\

    async fetchCreativeTests () {
      this.loading.launchGroups = true
      // Creative test uses min_event_date_for_creative instead of event_date
      const reportFilters = this.creativeReport.filters.length > 0 
        ? this.creativeReport.filters.map(filter => filter.map(q => q.key === 'event_date' ? { ...q, key: `min_event_date_for_creative` } : q))
        : null
      
      const query = reportFilters ?? [
        [{ key: 'min_event_date_for_creative', operation: 'greater_than', value: 'last_7_days' }],
        [{ key: 'min_event_date_for_creative', operation: 'less_than', value: 'today' }]
      ]

      try {
        const gridKpis = this.selectedColumns?.[this.GRAPH_TYPES.GRID]?.map(key => ({ key })) || []
        const uniqueKpis = [...new Set([...this.creativeReport.table_columns, ...gridKpis])]
        const payload = {
          group: 'ad_name',
          adAccountIds: this.selectedAccountIds,
          platform: this.getMetricsPlatform,
          selectedColumns: uniqueKpis,
          filters: query,
          currentWindow: {
            click: this.lens.click_attribution_window,
            view: this.lens.view_attribution_window
          },
          page: 1,
          limit: 150,
          sort: this.creativeReport.sorted_column
        }

        console.log('Fetch lighthouse groups payload', payload)
        
        const res = await LensAPI.Lens.Lighthouse.getGroups(this.lens.id, payload)
        this.categorizedData = res.data
        this.pagination = {
          ...this.pagination,
          totalItems: res.total,
          totalPages: res.pages
        }
      } catch (e) {
        console.error(e)
        this.$showAlert({ type: 'error', message: e.message })
      } finally {
        this.loading.launchGroups = false
      }
    },
    async fetchChartData () {
      this.loading.chartData = true
      // Creative test uses min_event_date_for_creative instead of event_date
      const reportFilters = this.creativeReport.filters.length > 0 
        ? this.creativeReport.filters.map(filter => filter.map(q => q.key === 'event_date' ? { ...q, key: `min_event_date_for_creative` } : q))
        : null
      
      const query = reportFilters ?? [
        [{ key: 'min_event_date_for_creative', operation: 'greater_than', value: 'last_7_days' }],
        [{ key: 'min_event_date_for_creative', operation: 'less_than', value: 'today' }]
      ]

      try {
        const payload = {
          group: 'ad_name',
          adAccountIds: this.selectedAccountIds,
          platform: this.getMetricsPlatform,
          filters: query,
          currentWindow: {
            click: this.lens.click_attribution_window,
            view: this.lens.view_attribution_window
          },
          page: 1,
          limit: 10,
          interval: 1,
          sort: this.creativeReport.sorted_column
        }

        const { data } = await LensAPI.Lens.Lighthouse.getChartData(this.lens.id, payload)
        const { max_metrics, insights, launched } = data
        this.maxChartValues = max_metrics
        this.categoryInsightCounts = insights
        this.chartData = launched
      } catch (e) {
        console.error(e)
        this.$showAlert({ type: 'error', message: e.message })
      } finally {
        this.loading.chartData = false
      }
    },
    // Only used for grid view
    handleAddMetric (key) {
      this.disableAddMetricFadeOut = true
      this.show.addMetricDropdown = false

      const newCols = [...this.selectedColumns[this.GRAPH_TYPES.GRID], key]
      this.selectedColumns[this.GRAPH_TYPES.GRID] = newCols
      const columnChange = { type: 'selected_columns', value: this.selectedColumns }
      this.handleCreativeReportChange(columnChange)
    },
    handleRemoveMetric (key) {
      const newCols = this.selectedColumns[this.GRAPH_TYPES.GRID].filter((col) => col !== key)
      this.selectedColumns[this.GRAPH_TYPES.GRID] = newCols
      const columnChange = { type: 'selected_columns', value: this.selectedColumns }
      this.handleCreativeReportChange(columnChange)
    },

    // ============================================================================ \\
    // ====================== LIGHTHOUSE CONFIG METHODS =========================== \\
    // ============================================================================ \\

    async fetchCreativeTargets () {
      try {
        this.loading.target = true
        const { data: lighthouseConfigs } = await LensAPI.Lens.Lighthouse.getAllConfig(this.lens.id)
        const userHasConfig = lighthouseConfigs.length !== 0
        this.isNewTarget = !userHasConfig
        if (userHasConfig) {
          this.rawLighthouseConfig = lighthouseConfigs[0]
        } else {
          this.show.targetModal = true
        }
      } catch (e) {
        this.$showAlert({ type: 'error', message: 'Unable to fetch creative targets' })
      } finally {
        this.loading.target = false
      }
    },
    async handleConfigSave (toUpdate) {
      // Handle local update
      this.rawLighthouseConfig = toUpdate
      if (this.isNewTarget) {
        await this.fetchCreativeReport()
      } else {
        this.fetchChartData()
      }
      this.show.targetModal = false
      this.isNewTarget = false
    },

    // ============================================================================ \\
    // ================= REPORT METHODS (type: creative-test) ===================== \\
    // ============================================================================ \\

    async fetchCreativeReport () {
      this.loading.creativeReport = true
      try {
        const query = {
          lens_id: this.$route.params.lensId,
          report_type: 'creative-test',
          limit: 10,
          page: 1
        }
        const { data } = await LensAPI.LensReport.getAll(query)
        const reportConfig = data?.[0]
        this.reportConfig = reportConfig
        this.originalReportConfig = _.cloneDeep(reportConfig)
        this.initCreativeReportData(reportConfig)
      } catch (e) {
        this.$showAlert({ type: 'error', message: e.message })
      } finally {
        this.loading.creativeReport = false
      }
    },
    async initCreativeReportData (config = null, doFetch = true) {
      const reportConfig = config ?? this.reportConfig
      const reportData = getReportStateFromConfig(reportConfig, this.getMetricsPlatform)
      this.reportId = reportData.id

      // Initialize preset
      const isColHashEqual = reportData.preset?.columns_hash === reportData.columns_hash
      const presetId = isColHashEqual ? reportData.preset?.id : null
      if (presetId) {
        this.updateIsCustomPreset(false)
        this.setPresetById({ id: presetId, shouldSelect: true })
      } else if (!reportData.preset_id) {
        this.updateIsCustomPreset(true)
      }
      const currentPreset = reportData?.preset || this.getAppliedPreset || this.getPresets[0]

      // Initialize Selected Graph Columns
      const selectedCols = {
        [this.GRAPH_TYPES.GRID]: reportData.selected_columns.grid,
        [this.GRAPH_TYPES.TABLE]: reportData.selected_columns.bar
      }
      this.selectedColumns = selectedCols

      // Initialize Table Columns
      const currentCols = reportData.table_columns.length > 0 
        ? reportData.table_columns 
        : currentPreset?.table_columns.map((col) => ({ key: col, is_pinned: false })) || []
      
      // Initialize Filters (aside from selected account ids)
      const dateQuery = reportData?.filters?.length > 0 ? reportData.filters : [
        [{ key: 'event_date', value: 'last_7_days', operation: 'greater_than' }], 
        [{ key: 'event_date', value: 'today', operation: 'less_than' }]
      ]
      this.appliedFilters = parseFiltersFromQuery(reportData?.filters)
      this.selectedDateRange = parseDateRangeFromQuery(dateQuery)

      // Build creative report object
      this.creativeReport = {
        group_by: reportData.group_by || 'ad_name',
        preset_id: currentPreset?.id,
        table_columns: currentCols,
        selected_columns: selectedCols,
        filters: reportData.filters || [],
        table_config: {
          colorFormat: reportData.table_config?.colorFormat ?? 'positive',
          showStatus: reportData.table_config?.showStatus ?? false,
          showTags: reportData.table_config?.showTags ?? false
        },
        sorted_column: reportData.sorted_column || { by: 'spend', order: 'desc' }
      }

      // Fetch lighthouse data with new report data
      if (doFetch) {
        await Promise.all([
          this.fetchCreativeTests(),
          this.fetchChartData()
        ])
      }
      this.firstInitComplete = true
    },
    async handleCreativeReportChange (change, fetchAll) {
      const { type, value, preventFetch = false } = change
      console.log(`%c creative change ${type}`, 'font-size: 14px; color: pink')
      const newConfig = applyChangeToReportConfig(this.reportConfig, { type, value })
      this.reportConfig = newConfig
      this.initCreativeReportData(newConfig, false)
      if (!preventFetch) {
        if (fetchAll) {
          await Promise.all([
            this.fetchCreativeTests(),
            this.fetchChartData()
          ])
        } else {
          await this.fetchCreativeTests()
        }
      }
    },
    async handleSaveReport (toUpdate) {
      console.log('%c SAVING REPORT', 'color: orange; font-size: 18px')
      // Init async loading state
      this.editLoading = { type: null, value: true }
      try {
        const reportId = this.reportId
        const toUpdateFormatted = transformFieldValueAllPlatforms(toUpdate, 'selected_columns', (selectedCols) => ({
          grid: selectedCols?.[this.GRAPH_TYPES.GRID],
          bar: selectedCols?.[this.GRAPH_TYPES.TABLE]
        }))
        await LensAPI.LensReport.update(reportId, toUpdateFormatted)

        // Show success message & wait before dismiss & refetch
        this.editLoading = { type: 'success', value: false }
        setTimeout(async () => {
          this.editLoading = { type: null, value: false }
        }, 1500)

        this.originalReportConfig = _.cloneDeep(this.reportConfig)
      } catch (e) {
        this.$showAlert({ type: 'error', message: e.message })
        this.editLoading = { type: null, value: false }
      }
    },
    async handleReportCancel (shouldFetch = false) {      
      const configCopy = _.cloneDeep(this.originalReportConfig)
      const oldLaunchDate = this.selectedDateRange
      this.reportConfig = configCopy
      this.initCreativeReportData(configCopy, false)

      const isDateDiff = new Date(oldLaunchDate).getTime() !== new Date(this.selectedDateRange).getTime()
      if (shouldFetch) {
        if (isDateDiff) {
          await Promise.all([
            this.fetchCreativeTests(),
            this.fetchChartData()
          ])
          return
        }
        await this.fetchCreativeTests()
      }
    },

    // ============================================================================ \\
    // ============================ FILTER METHODS ================================ \\
    // ============================================================================ \\

    handleFiltersChange (filters, dateRange) {
      if (!this.firstInitComplete) return

      // Check if the applied filters match the insight category conditions
      // E.g., if filters match what the lighthouse config defines as winning, set the selected category group to 'winners'
      if (this.lighthouseConfig) {
        const { scaling, winners, losers } = this.lighthouseConfig
        const scalingCondition = scaling?.[0]?.[0]
        const winnersCondition = winners?.[0]?.[0]
        const losersCondition = losers?.[0]?.[0]

        const conditionMatches = (filterCondition, insightCondition, inverseOperation = false) => {
          const { operation, property, value } = filterCondition
          const insightOperation = inverseOperation 
            ? (insightCondition.operation === 'greater_than' ? 'less_than' : 'greater_than') 
            : insightCondition.operation
          return (
            operation.key === insightOperation
            && property.key === insightCondition.key
            && value === insightCondition.value
          )
        }

        if (filters.length === 1 && conditionMatches(filters?.[0]?.[0], losersCondition)) {
          this.selectedCategoryGroup = 'losers'
        } else if (
          filters.length === 2 
          && conditionMatches(filters?.[0]?.[0], scalingCondition) 
          && conditionMatches(filters?.[1]?.[0], winnersCondition, true)
        ) {
          this.selectedCategoryGroup = 'scaling'
        } else if (
          filters.length === 2
          && conditionMatches(filters?.[0]?.[0], scalingCondition)
          && conditionMatches(filters?.[1]?.[0], winnersCondition)
        ) {
          this.selectedCategoryGroup = 'winners'
        } else {
          this.selectedCategoryGroup = 'all'
        }
      }

      const filterQuery = createFilterQuery(filters, dateRange)
      this.reportConfig = applyChangeToReportConfig(this.reportConfig, { type: 'filters', value: filterQuery })
      this.creativeReport.filters = filterQuery
      this.fetchCreativeTests()
      this.fetchChartData()
    },
    handleFilterCreated (filterConditions) {
      const newFilters = [...this.appliedFilters, filterConditions]
      this.appliedFilters = newFilters
    },
    handleFilterRemoved (index) {
      const newFilters = this.appliedFilters.filter((_, i) => i !== index)
      this.appliedFilters = newFilters
    },
    handleFilterOptionSelected (option) {
      this.builderInitFilterOption = option
      this.showFilterSelector = false
    },
    handleEditAppliedFilter (filterConditions, index) {
      const newFilters = [...this.appliedFilters]
      newFilters[index] = filterConditions
      this.appliedFilters = newFilters
    },
    toggleFilterCreation () {
      if (this.isBuildingFilter) {
        this.closeFilterCreation()
      } else {
        this.showFilterSelector = true
      }
    },
    closeFilterCreation () {
      this.showFilterSelector = false
      this.builderInitFilterOption = null
    },
    toggleEditAppliedFilter (index) {
      if (this.editingFilterIndex !== index) {
        this.editingFilterIndex = index
      } else {
        this.editingFilterIndex = null
      }
    },
    handleFilterByCategory (category) {
      if (category === 'all') { // 'All' can only be clicked if we are in another category group
        this.appliedFilters = []
        return
      }

      this.appliedFilters = this.targetCategoryFilterOptions[category]
    },

    // ============================================================================ \\
    // ============================ DRAWER METHODS ================================ \\
    // ============================================================================ \\

    createTopPayload (groupKey, groupBy, additionalColumns = [], isCreativeInsightsRequest = false) {
      const groupFilter = [{ key: 'group_by', value: groupKey, operation: 'equals' }]
      const filterQuery = createFilterQuery(this.appliedFilters, this.selectedDateRange)
      if (!isCreativeInsightsRequest) filterQuery.push(groupFilter)

      // In case we want to fetch additional columns that are not currently used in the table
      const selectedColumns = [...new Set([
        ...additionalColumns.map(key => ({ key, is_pinned: false })),
        ...this.creativeReport.table_columns,
        { key: getGoalMetricKey(this.lens), is_pinned: false } // Make sure goal metric is always included
      ])]

      // Exclude foreplay metrics in creative insights requests (we will always include them in a separate card)
      const payloadSelectedColumns = isCreativeInsightsRequest
        ? selectedColumns.filter(col => this.getMetricLookup?.[col.key]?.parent_group !== 'foreplay_metrics')
        : selectedColumns

      // Creative test uses min_event_date_for_creative instead of event_date (for now - Nov 4th, 2025)
      const formattedFilterQuery = [...filterQuery].map(filter => filter.map(q => q.key === 'event_date' ? { ...q, key: `min_event_date_for_creative` } : q))
      const payload = {
        adAccountIds: this.selectedAccountIds,
        platform: this.getMetricsPlatform,
        group: groupBy || this.creativeReport.group_by,
        currentWindow: {
          click: this.creativeReport?.click_attribution_window || '7d',
          view: this.creativeReport?.view_attribution_window || '1d'
        },
        filters: formattedFilterQuery,
        selectedColumns: payloadSelectedColumns
      }
      return payload
    },
    async handleShowBreakdown (row) {
      const groupKey = row.creative_hash
      this.loading.groupBreakdown = true
      try {
        const payload = this.createTopPayload(groupKey, 'ad_name')
        const res = await LensAPI.Lens.Lighthouse.getGroupAds(this.lens.id, payload, 'top-performing')
        const group = res.data.ad_groups[0]
        this.groupBreakdownData = {
          ...group,
          previous_period: group.previous_period || {},
          summary_stats: res.data.summary_stats || {}
        }
      } catch (e) {
        if (axios.isCancel(e)) {
          console.log('New query detected: Prev request canceled')
        } else {
          console.error(e)
          this.$showAlert({
            type: 'error',
            message: 'An unexpected error occurred'
          })
        }
      } finally {
        this.loading.groupBreakdown = false
      }
    },
    handleCloseBreakdown () {
      LensAPI.AdGroups.abortBreakdownRequest()
      this.groupBreakdownData = null
    },
    async handleShowDetailsDrawer (row, additionalColumns = []) {
      if (additionalColumns.length) {
        this.loading.adDetailsMetrics = true
      } else {
        this.loading.adDetails = true
        this.adDetailsRow = row
      }
      const payload = this.createTopPayload(row?.group_by || row?.min_event_date, null, additionalColumns, true)

      try {  
        const firstAd = row?.ads?.[0] || row
        const { groupBy, ...query } = payload
        const idPayload = {
          lensId: this.$route.params.lensId,
          creativeId: firstAd.creative_id,
          creativeHash: firstAd?.creative_hash,
          adId: firstAd.ad_id
        }
        const [adDetails, creativeTags] = await Promise.all([
          LensAPI.AdGroups.getLensCreativeGroup(idPayload, query),
          LensAPI.Tags.getTagsByCreativeHash(this.$route.params?.lensId, firstAd?.creative_hash)
        ])
        this.adDetailsData = {
          ...row,
          ...adDetails.data,
          aiTransaction: adDetails.data.ai_transaction,
          creativeTags: creativeTags?.data,
          // For share purposes
          detailsQuery: {
            groupBy: this.creativeReport?.group_by,
            query,
            date: this.selectedDateRange
          },
          isCreativeGroup: true
        }
      } catch (e) {
        if (axios.isCancel(e)) {
          console.log('New query detected: Prev request canceled')
        } else {
          console.error(e)
          this.$showAlert({
            type: 'error',
            message: 'An unexpected error occurred'
          })
        }
      } finally {
        this.loading.adDetails = false
        this.loading.adDetailsMetrics = false
      }
    },

    // ============================================================================ \\
    // ================================ UI METHODS ================================ \\
    // ============================================================================ \\

    getEditFilterDropdownPositionClasses (index) {
      // Determines whether to left-align, center, or right-align the edit filter dropdown
      const filterElement = this.$refs[`applied-filter-${index}`][0]
      const mainLayoutParent = document.querySelector('.main-layout')
      if (!filterElement || !mainLayoutParent) return 'left-0'

      const mainLayoutRect = mainLayoutParent.getBoundingClientRect()
      const filterElmRect = filterElement.getBoundingClientRect()
      const filterElmPos = (filterElmRect.left - mainLayoutRect.left) + (filterElmRect.width / 2)
      
      const sectorWidth = mainLayoutRect.width / 3
      const sectorBoundaries = [sectorWidth, sectorWidth * 2]

      if (filterElmPos < sectorBoundaries[0]) {
        return 'left-0'
      } else if (filterElmPos < sectorBoundaries[1]) {
        return 'left-1/2 transform -translate-x-1/2'
      } else {
        return 'right-0'
      }
    },
    handleTargetClose () {
      this.show.targetModal = false
      if (this.isNewTarget) {
        // nav back to dashboard
        this.$emit('navToDashboard')
      }
    },
    handleScrollStateChange (scrollState) {
      this.tableScrollState = scrollState
    },
    handleTableScroll (direction) {
      if (!this.$refs.metricTable) return

      if (direction === 'left') {
        this.$refs.metricTable.scrollLeft()
      } else if (direction === 'right') {
        this.$refs.metricTable.scrollRight()
      }
    }
  }
}

const meetsCondition = (creative, { key, value, operation }) => {
  const adValue = creative[key]
  if (adValue == null) return false
  if (operation === 'greater_than') return adValue > value
  if (operation === 'less_than') return adValue < value
  return false
}
</script>

<style scoped>
.base-btn {
  box-shadow: 0px 1px 2px 0px rgba(4, 26, 75, 0.13),
    0px 0px 0px 1px rgba(0, 56, 108, 0.08);
  transition: box-shadow 100ms ease-in-out, background-color 100ms ease-in-out;
  border-radius: 6px;
}

.base-btn:hover {
  box-shadow: 0px 1px 2px 0px rgba(4, 26, 75, 0.17),
    0px 0px 0px 1px rgba(0, 56, 108, 0.12);
}

.graph-selector-atom {
  padding: 2px;
  border-radius: 4px;
  background-color: transparent;
  box-shadow: none;
  transition: background-color 100ms ease-in-out, box-shadow 100ms ease-in-out;
}

.graph-selector-atom.active,
.graph-selector-atom.active:hover {
  background-color: white;
  box-shadow: 0px 3px 3px -1.5px rgba(6, 7, 16, 0.04), 0px 1.5px 1.5px -0.75px rgba(6, 7, 16, 0.08), 0px 0.25px 1.5px 0.75px rgba(6, 7, 16, 0.02);
}

.date-selector-dropdown {
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  z-index: 500;
}

.selected-kpi {
  display: flex;
  align-items: center;
  padding: 2px;
  border-radius: 6px;
  background-color: white;
  box-shadow: 0px 3px 3px -1.5px rgba(6, 7, 16, 0.04), 0px 1.5px 1.5px -0.75px rgba(6, 7, 16, 0.08), 0px 0px 0.25px 0.75px rgba(6, 7, 16, 0.04);
}

.add-metric-dropdown {
  position: absolute;
  left: 0;
  top: 100%;
  z-index: 9999;
  margin-top: 4px;
}

.filter-selector-dropdown {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  z-index: 500;
}
.filter-query-builder {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  z-index: 500;
}
.edit-filter-query-builder {
  position: absolute;
  top: calc(100% + 4px);
  z-index: 500;
}

.add-metric-enter-active,
.add-metric-leave-active {
  transition: opacity 0.2s, transform 0.2s;
}

.add-metric-enter-from,
.add-metric-enter,
.add-metric-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}

.add-metric-enter-to, .add-metric-leave-from {
  opacity: 1;
  transform: translateY(0);
}

.v-enter-active, .v-leave-active {
  transition: opacity 100ms ease-in-out;
}
.v-enter-from, .v-enter, .v-leave-to {
  opacity: 0;
}
.v-enter-to, .v-leave-from {
  opacity: 1;
}

.fade-in {
  animation: fadeIn 150ms ease-in-out;
}
@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}
</style>